/**
 * hassYamlGenerator.js
 *
 * Entry point: generateObjectYaml(options)
 * where options = { page_id, hasp_id, object, hass_config, useTag }
 *
 * Event generation is now handled by the Automation Engine pipeline:
 *   hass_config.events[]
 *     → migrateHassConfigToAst (LegacyEventsToAst.js)
 *     → AutomationCompiler
 *     → HomeAssistantGenerator
 *
 * Property generation (hass_config.properties[]) remains here; properties are
 * state subscriptions, not automations, and are not part of the Automation AST.
 */

import { loadAutomationModels }   from '../automation/migration/LegacyEventsToAst.js';
import { AutomationCompiler }     from '../automation/compiler/AutomationCompiler.js';
import { HomeAssistantGenerator } from '../automation/generators/HomeAssistantGenerator.js';
import { getEffectivePrimaryEntity } from '../automation/primaryEntity.js';

// ── Dataset helpers ───────────────────────────────────────────────────────────

/**
 * Parse a human-readable history string to a number of hours.
 * Supports: "N hours", "N hour", "N days", "N day", "N weeks", "N week", "N minutes", "N minute".
 * Returns null if the string cannot be parsed.
 */
export function parseHistoryHours(str) {
    if (str == null) return null;
    const s = String(str).trim();
    if (!s) return null;
    // Bare number — treat as hours (legacy format)
    if (/^\d+(?:\.\d+)?$/.test(s)) return parseFloat(s);
    const m = s.match(/^(\d+(?:\.\d+)?)\s*(\w+)$/);
    if (!m) return null;
    const n = parseFloat(m[1]);
    const u = m[2].toLowerCase();
    if (u === 'hour'   || u === 'hours')   return n;
    if (u === 'day'    || u === 'days')    return n * 24;
    if (u === 'week'   || u === 'weeks')   return n * 168;
    if (u === 'minute' || u === 'minutes') return n / 60;
    return null;
}

/**
 * Parse a human-readable interval string to a number of minutes.
 * Supports: "N minutes", "N minute", "N hours", "N hour".
 * Returns null if the string cannot be parsed.
 */
export function parseIntervalMinutes(str) {
    if (str == null) return null;
    const s = String(str).trim();
    if (!s) return null;
    // Bare number — treat as minutes (legacy format)
    if (/^\d+(?:\.\d+)?$/.test(s)) return parseFloat(s);
    const m = s.match(/^(\d+(?:\.\d+)?)\s*(\w+)$/);
    if (!m) return null;
    const n = parseFloat(m[1]);
    const u = m[2].toLowerCase();
    if (u === 'minute' || u === 'minutes') return n;
    if (u === 'hour'   || u === 'hours')   return n * 60;
    return null;
}

/**
 * Generate the `datasets:` YAML section from an array of validated dataset objects.
 *
 * Dataset structure: { id, name, history_hours, interval, unit, type, series: [{label, entity, color}] }
 * history_hours is in hours; interval is in seconds.
 * Returns an empty string when the array is empty.
 */
export function generateDatasetSectionYaml(datasets) {
    if (!datasets || datasets.length === 0) return '';
    const lines = ['datasets:'];
    for (const ds of datasets) {
        lines.push(`  - id: ${ds.id}`);
        lines.push(`    name: "${ds.name}"`);
        if (ds.history_hours != null) lines.push(`    history_hours: ${ds.history_hours}`);
        if (ds.interval      != null) lines.push(`    interval: ${ds.interval}`);
        if (ds.unit)                  lines.push(`    unit: "${ds.unit}"`);
        if (ds.type)                  lines.push(`    type: "${ds.type}"`);
        if (ds.series && ds.series.length > 0) {
            lines.push(`    series:`);
            for (const s of ds.series) {
                lines.push(`      - label: "${s.label ?? ''}"`);
                lines.push(`        entity: ${s.entity}`);
                if (s.color) lines.push(`        color: "${s.color}"`);
            }
        }
    }
    return lines.join('\n') + '\n';
}

// ── Automation Engine pipeline ────────────────────────────────────────────────

const _compiler  = new AutomationCompiler();
const _generator = new HomeAssistantGenerator();

/**
 * Generate HA component YAML for one widget.
 *
 * The events section is now produced by the Automation Engine:
 *   hass_config.events[] → AST → Compiler → HomeAssistantGenerator
 *
 * The properties section is forwarded to HomeAssistantGenerator unchanged;
 * property bindings are state subscriptions, not automations.
 *
 * Public API is unchanged — existing callers require no modification.
 */
export function generateObjectYaml({ page_id, hasp_id, object, hass_config, useTag = false, validationErrors, configOf }) {
    const models                = loadAutomationModels({ page_id, hasp_id, object, hass_config });
    // configOf: how to read a node's HA configuration while resolving an
    // inherited Primary Entity (default: node.hass_config).
    const primaryEntity         = getEffectivePrimaryEntity(object, { configOf });
    const { models: compiled }  = _compiler.compile(models, { primaryEntity });
    return _generator.generate({ page_id, hasp_id, object, hass_config, models: compiled, useTag, validationErrors });
}
